import {
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';

import {
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';

import {
  EMPTY,
  catchError,
  filter,
  switchMap,
} from 'rxjs';

import {
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';

import {
  Sidebar,
} from './shared/components/sidebar/sidebar';

import {
  AuthService,
} from './core/auth/services/auth.service';

import {
  LoaderService,
} from './shared/services/loader.service';

import {
  MaterialModule,
} from './shared/material.module';


import {
  PayrollPeriodAlertService,
  StatutPaieResponse,
} from './shared/components/payroll-period-alert/payroll-period-alert.service';
import { PayrollPeriodAlertComponent } from './shared/components/payroll-period-alert/payroll-period-alert';
import { NotificationBellComponent } from './features/notifications/notification-bell.component';
import { SystemMessagesComponent } from './shared/components/system-messages/system-messages.component';

@Component({
  selector: 'app-root',

  imports: [
    MaterialModule,
    RouterOutlet,
    Sidebar,
    PayrollPeriodAlertComponent,
    NotificationBellComponent,
    SystemMessagesComponent,
  ],

  templateUrl: './app.html',

  styleUrl: './app.scss',
})
export class App {

  protected title = 'ratibi';

  readonly loader = inject(LoaderService);

  private readonly router = inject(Router);

  private readonly destroyRef = inject(DestroyRef);

  private readonly payrollPeriodAlertService =
    inject(PayrollPeriodAlertService);

  /**
   * Prevents repeated API calls during navigation.
   *
   * It becomes true after the API has been requested
   * for the current authenticated session.
   *
   * It is reset after logout.
   */
  private payrollAlertLoaded = false;

  readonly latestSalaryState =
    signal<StatutPaieResponse | null>(null);

  readonly showPayrollAlert =
    computed(() => {

      const state =
        this.latestSalaryState();

      if (!state) {
        return false;
      }

      if (!this.canViewPayrollAlert()) {
        return false;
      }

      const now = new Date();

      const currentPeriod =
        now.getFullYear() * 12 +
        now.getMonth();

      const responsePeriod =
        state.annee * 12 +
        (state.mois - 1);

      return responsePeriod < currentPeriod;
    });

  constructor(
    public auth: AuthService,
  ) {
    this.initializePayrollAlert();
  }

  private canViewPayrollAlert(): boolean {

    if (!this.auth.isLoggedIn) {
      return false;
    }

    const roles =
      this.auth.roles ?? [];

    return (
      roles.includes('ROLE_RH') ||
      roles.includes(
        'ROLE_ADMIN_ENTREPRISE',
      )
    );
  }

  private initializePayrollAlert(): void {

    /**
     * We keep NavigationEnd ONLY to detect:
     *
     * - login -> authenticated application
     * - logout -> public application
     *
     * Navigation itself no longer causes another
     * API call after the first successful loading.
     */
    this.router.events
      .pipe(

        filter(
          event =>
            event instanceof NavigationEnd,
        ),

        switchMap(() => {

          /**
           * User logged out.
           *
           * Reset everything so that a future login
           * can request the payroll state again.
           */
          if (!this.auth.isLoggedIn) {

            this.payrollAlertLoaded = false;

            this.latestSalaryState.set(
              null,
            );

            return EMPTY;
          }

          /**
           * User is authenticated but isn't concerned
           * by the payroll alert.
           */
          if (!this.canViewPayrollAlert()) {

            this.latestSalaryState.set(
              null,
            );

            return EMPTY;
          }

          /**
           * Already loaded for this login session.
           *
           * From here every navigation:
           *
           * employees
           * settings
           * payroll
           * timesheets
           * ...
           *
           * does NOTHING.
           */
          if (this.payrollAlertLoaded) {
            return EMPTY;
          }

          const organisationId =
            this.auth.organizationId;

          if (!organisationId) {
            return EMPTY;
          }

          /**
           * Mark it before calling the API so two
           * very close NavigationEnd events cannot
           * accidentally trigger two requests.
           */
          this.payrollAlertLoaded = true;

          return this
            .payrollPeriodAlertService
            .getStatutPaie(
              organisationId,
              null,
              null,
            )
            .pipe(

              catchError(error => {

                console.error(
                  'Erreur lors de la récupération du statut de la paie',
                  error,
                );

                /**
                 * If the request failed, allow another
                 * navigation to retry it.
                 *
                 * If you DON'T want automatic retry,
                 * remove this line.
                 */
                this.payrollAlertLoaded = false;

                return EMPTY;
              }),
            );
        }),

        takeUntilDestroyed(
          this.destroyRef,
        ),
      )
      .subscribe(response => {

        this.latestSalaryState.set(
          response.data,
        );
      });
  }
}
